import type * as RemixReact from "@remix-run/react";
import { Path } from "@vendrinc/frontend-ts";
import * as Diff from "deep-object-diff";
import { validate as validateUuid } from "uuid";
import { z } from "zod";
import { ContractTypeParam } from "~/domain/contracts";
import { TypeOfPurchase } from "~/domain/privateOffers/answers";
import { RequestTypeParam } from "~/domain/requestType";

export function home() {
  return absolute();
}

export function careers() {
  return absolute(["careers"]);
}

export function buyerGuides() {
  return absolute(["buyer-guides"]);
}

export function vendrVerified() {
  return absolute(["vendr-verified"]);
}

export function chromeExtensionMarketingPage() {
  return absolute(["software-prices"]);
}

export function marketplace() {
  return absolute(["marketplace"]);
}

export function partners() {
  return {
    index: () => absolute(["partners"]),
    actionLinks: () => absolute(["partners", "action-links"]),
    apiActionLinksMode: (actionLinkId: string) =>
      absolute(["partners", "api", "v1", "action-links", actionLinkId, "mode"]),
    viewActionLink: (actionLinkId: string) =>
      absolute(["partners", "action-links", actionLinkId]),
    getCreateActionLinkPath: () =>
      absolute(["partners", "action-links", "create"]),
    getEditActionLinkPath: (actionLinkId: string) =>
      absolute(["partners", "action-links", actionLinkId, "edit"]),
    login: () => absolute(["partners", "login"]),
    logout: ({ origin }: { origin: string }) => {
      const queryParams: Record<string, string | number | boolean> = {
        origin,
      };

      return absolute(["partners", "logout"], queryParams);
    },
  };
}

export const routes = {
  api: {
    createShareToken: () => absolute(["api", "share-tokens"]),
    priceChecksV2Polling: () => absolute(["api", "price-checks-v2", "polling"]),
  },
  customers: () => absolute(["customers"]),
  getDemo: (params?: { demo_source?: string }) =>
    absolute(["get-demo"], params ?? {}),
  pricingApiDemo: (params?: { demo_source?: string }) =>
    absolute(["pricing-api-demo"], params ?? {}),
  newPriceCheck: ({
    companySlug,
    companyId,
  }: {
    companySlug?: string;
    companyId?: string;
    previousScopeId?: string;
  }) => {
    const query: Record<string, string> = {};
    if (companySlug) {
      query.companySlug = companySlug;
    }
    if (companyId) {
      query.companyId = companyId;
    }

    return absolute(["new-price-check"], query);
  },
  vendrPro: () => absolute(["vendr-pro"]),
  enablement: () => absolute(["enablement"]),
  pricing: () => absolute(["pricing"]),
  pricingCredits: () => absolute(["pricing", "credits"]),
  pricingApi: () => absolute(["pricing-api"]),
  negotiationApi: () => absolute(["negotiation-api"]),
  shareTokens: {
    shareToken: ({ shareTokenId }: { shareTokenId: string }) =>
      absolute(["share-tokens", shareTokenId]),
  },
  feedbackApi: () => absolute(["api", "feedback"]),
};

export function company(slug: string, productId?: string) {
  return absolute(
    ["marketplace", slug],
    productId ? { product: productId } : {},
  );
}

type NewRequestType = { slugOrId: string } & (
  | {
      requestType: RequestTypeParam;
      priceEstimateId?: string;
      contractType?: ContractTypeParam;
    }
  | {
      requestType: RequestTypeParam;
    }
);

export function newRequest(args: NewRequestType) {
  const queryParams: Record<string, string | number | boolean> = {
    requestType: args.requestType,
  };

  if ("priceEstimateId" in args && args.priceEstimateId) {
    queryParams.priceEstimateId = args.priceEstimateId;
  }

  if ("contractType" in args && args.contractType) {
    queryParams.contractType = args.contractType;
  }

  return absolute(["marketplace", args.slugOrId], queryParams);
}

export function buyerGuide(companyIdOrSlug: string) {
  return absolute(["buyer-guides", companyIdOrSlug], {});
}

export function startRequest({
  priceEstimateId,
  companySlugOrId,
  haveRenewalQuote,
  typeOfPurchase,
}: {
  priceEstimateId?: string;
  requestType?: "priceEstimate" | "priceCheck" | "quote";
  companySlugOrId: string;
  productId?: string;
  haveRenewalQuote?: boolean;
  typeOfPurchase?: string;
  newPriceCheckFlow?: boolean;
  returnPage?: "buyer-guide" | "company";
}) {
  return haveRenewalQuote
    ? routes.newPriceCheck(
        validateUuid(companySlugOrId)
          ? {
              companyId: companySlugOrId,
            }
          : {
              companySlug: companySlugOrId,
            },
      )
    : newRequest({
        slugOrId: companySlugOrId,
        requestType: RequestTypeParam.PriceEstimate,
        contractType:
          typeOfPurchase === TypeOfPurchase.NewPurchase
            ? ContractTypeParam.NewPurchase
            : ContractTypeParam.Renewal,
        priceEstimateId,
      });
}

export function intelligence() {
  return absolute(["intelligence"]);
}

export function buyWithAws() {
  return absolute(["buy-with-aws"]);
}

export function newPriceCheck() {
  return absolute(["price-check", "new"]);
}

export function priceChecks() {
  return absolute(["vendr-price-checks"]);
}

export function procurement() {
  return absolute(["procurement"]);
}

export function login(
  options: { redirectBackTo?: RemixReact.Path | string } = {},
) {
  const queryParams = () => {
    if (options.redirectBackTo) {
      const requestedRedirectUrl: string = redirectUrl(options.redirectBackTo);
      return { redirect_url: requestedRedirectUrl } as Record<string, string>;
    } else {
      return {};
    }
  };

  return absolute(["auth", "login"], queryParams());
}

export function logout(options: { redirectBackTo?: RemixReact.Location } = {}) {
  const queryParams: Record<string, string | number | boolean> =
    options.redirectBackTo ? { origin: options.redirectBackTo.pathname } : {};

  return absolute(["auth", "logout"], queryParams);
}

export function signup(
  options: { redirectBackTo?: RemixReact.Path | string } = {},
) {
  const queryParams = () => {
    if (options.redirectBackTo) {
      const requestedRedirectUrl: string = redirectUrl(options.redirectBackTo);
      return { redirect_url: requestedRedirectUrl } as Record<string, string>;
    } else {
      return {};
    }
  };

  return absolute(["auth", "signup"], queryParams());
}

function redirectUrl(redirectBackTo: RemixReact.Path | string) {
  const requestedRedirectUrl: string =
    typeof redirectBackTo === "string"
      ? redirectBackTo
      : `${redirectBackTo.pathname}${redirectBackTo.search}`;

  return requestedRedirectUrl;
}

export function organizationSelect() {
  return absolute(["auth", "organization-select"]);
}

export function categories() {
  return absolute(["categories"]);
}

export function category(slug: string) {
  return absolute(["categories", slug]);
}

export function subCategory({
  slug1,
  slug2,
  page = 1,
}: {
  slug1: string;
  slug2: string;
  page?: number;
}) {
  return absolute(["categories", slug1, slug2], {
    page,
  });
}

export function connectApi() {
  return absolute(["connect-api"]);
}

export function saasReport() {
  return absolute(["insights", "saas-trends-report-2025"]);
}

export const api = {
  companySearch: {
    get: (query: string) => absolute(["api", "company-search"], { query }),
  },
  enablementSearch: {
    get: (q: string) => absolute(["api", "enablement-search"], { q }),
  },
  documents: {
    create: () => absolute(["api", "documents"]),
    update: () => absolute(["api", "documents"]),
  },
  pollPriceChecks: () => absolute(["api", "price-checks", "polls"]),
  webhooks: {
    publicApi: () => absolute(["api", "webhooks", "public-api"]),
  },
};

/**
 * Constructs a relative URL from the given path segments and query parameters.
 *
 * @param pathSegments - An array of strings representing the path segments to be joined.
 * @param queryParams - An object where keys and values represent the query parameters to be appended.
 * @returns A string representing the constructed URL.
 */
const relative = (
  pathSegments: string[] = [],
  queryParams: Record<string, string | number | boolean> = {},
): string => {
  const path = pathSegments
    .map((segment) => encodeURIComponent(segment))
    .join("/");

  const queryString = Object.entries(queryParams)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("&");

  const url = `${path}${queryString ? "?" + queryString : ""}`;

  return url;
};

const absolute = (
  pathSegments: string[] = [],
  queryParams: Record<string, string | number | boolean> = {},
): string => {
  return `/${relative(pathSegments, queryParams)}`;
};

export const fullUrl = (domain: string, path: string) => {
  return new URL(path, `https://${domain}`).toString();
};

export type PurchaseIntentProps = {
  id?: string;
};

export type PurchaseIntentParams = {
  page: number;
  sort: PurchaseIntentSortableColumnParam;
  direction: "asc" | "desc";
  search: string;
  seller: string[] | null;
  purchaseIntent: PurchaseIntentTypeParam[] | null;
  intentSignal: PurchaseIntentSignalParam[] | null;
  hasVerifiedVisitors: boolean | null;

  productFamilyIds: string[] | null;
  productIds: string[] | null;
  limit: number;
  offset: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
};

export const purchaseIntentSortableColumnParamDecoder = z.enum([
  "captured",
  "company",
  "seller",
  "product",
]);

export type PurchaseIntentSortableColumnParam = z.infer<
  typeof purchaseIntentSortableColumnParamDecoder
>;

export const purchaseIntentTypeParamDecoder = z.enum([
  "price-estimate",
  "price-check",
  "catalog-view-verified",
  "catalog-view",
]);

export type PurchaseIntentTypeParam = z.infer<
  typeof purchaseIntentTypeParamDecoder
>;

export const purchaseIntentSignalParamDecoder = z.enum([
  "high",
  "medium",
  "low",
]);

export type PurchaseIntentSignalParam = z.infer<
  typeof purchaseIntentSignalParamDecoder
>;

/**
 * Decoder for comma-separated string values into string arrays.
 * Takes a comma-separated string and transforms it into an array of strings.
 * Empty values are filtered out.
 * If input is empty string, returns empty array.
 *
 * @example
 * "a,b,c" -> ["a", "b", "c"]
 * "a,,c" -> ["a", "c"]
 * "" -> []
 */
const commaSeparatedArrayDecoder = z
  .string()
  .transform((val) => val.split(",").filter(Boolean))
  .nullable()
  .default(null);

const commaSeparatedPurchaseIntentParamDecoder = z
  .string()
  .transform((val) => val.split(",").filter(Boolean))
  .pipe(z.array(purchaseIntentTypeParamDecoder))
  .nullable()
  .default(null);

const commaSeparatedPurchaseIntentSignalParamDecoder = z
  .string()
  .transform((val) => val.split(",").filter(Boolean))
  .pipe(z.array(purchaseIntentSignalParamDecoder))
  .nullable()
  .default(null);

const booleanDecoder = z
  .string()
  .transform((val) => {
    switch (val) {
      case "true":
        return true;
      case "false":
        return false;
      default:
        return null;
    }
  })
  .nullable()
  .default(null);

/**
 * Decoder for purchase intent parameters with defaults.
 * All fields must have defaults since they are optional in URLs.
 * - page: Defaults to 1 for first page
 * - sort: Defaults to "captured" column
 * - direction: Defaults to descending order
 *
 * These are the defaults that will propagate through the rest
 * of the application.
 */
export const purchaseParamsDecoder = z.object({
  page: z.coerce.number().default(1),
  sort: purchaseIntentSortableColumnParamDecoder.default("captured"),
  direction: z.enum(["asc", "desc"]).default("asc"),
  search: z.string().default(""),
  seller: commaSeparatedArrayDecoder,
  purchaseIntent: commaSeparatedPurchaseIntentParamDecoder,
  intentSignal: commaSeparatedPurchaseIntentSignalParamDecoder,
  hasVerifiedVisitors: booleanDecoder,
  productFamilyIds: commaSeparatedArrayDecoder,
  productIds: commaSeparatedArrayDecoder,
  limit: z.coerce.number().default(10),
  offset: z.coerce.number().default(0),
  sortBy: z.string().default(""),
  sortOrder: z.enum(["asc", "desc"]).default("asc"),
});

export const defaultPurchaseIntentParams: PurchaseIntentParams =
  purchaseParamsDecoder.parse({});

export const isDefaultPurchaseIntentParams = (params: PurchaseIntentParams) =>
  JSON.stringify(params) === JSON.stringify(defaultPurchaseIntentParams);

/**
 * Checks if any filter parameters are set.
 * This can be used to determine if the reset button should be shown.
 */
export const hasPurchaseIntentFilterParams = (
  params: PurchaseIntentParams,
): boolean =>
  [
    params.search !== "",
    params.seller !== null,
    params.purchaseIntent !== null,
    params.intentSignal !== null,
    params.hasVerifiedVisitors !== null,

    params.productFamilyIds !== null,
    params.productIds !== null,
    params.limit !== 0,
    params.offset !== 0,
    params.sortOrder !== "asc",
    params.sortBy !== "",
  ].some((value) => value);

/**
 * Resets all filter parameters to their defaults. Make sure to keep
 * the page order and direction the same as the current params.
 *
 * This can be used to reset the filters when the user clicks the reset button.
 */
export const resetPurchaseIntentFilterParams = (
  params: PurchaseIntentParams,
): PurchaseIntentParams => ({
  ...defaultPurchaseIntentParams,
  ...{
    direction: params.direction,
    sort: params.sort,
  },
});

export function purchaseIntent(
  params: PurchaseIntentProps &
    PurchaseIntentParams = defaultPurchaseIntentParams,
) {
  const pathParams = {
    page: params.page,
    sort: params.sort,
    direction: params.direction,
    search: params.search,
    seller: params.seller?.join(","),
    purchaseIntent: params.purchaseIntent?.join(","),
    intentSignal: params.intentSignal?.join(","),
    hasVerifiedVisitors:
      params.hasVerifiedVisitors === null
        ? null
        : params.hasVerifiedVisitors
          ? "true"
          : "false",

    productFamilyIds: params.productFamilyIds?.join(","),
    productIds: params.productIds?.join(","),
    limit: params.limit,
    offset: params.offset,
    sortBy: params.sortBy,
  };

  // Only include parameters that differ from the defaults to keep URLs clean
  // For example, if page=1 (default), it won't be included in the URL
  // This helps maintain shorter, more readable URLs while preserving functionality
  const withoutDefaults = difference({
    original: defaultPurchaseIntentParams,
    updated: pathParams,
  });

  const path = params.id
    ? ["sellers", "purchase-intent", params.id]
    : ["sellers", "purchase-intent"];

  return Path.absolute(path, withoutDefaults);
}

export function settings() {
  return Path.absolute(["sellers", "settings"]);
}

const difference = ({
  original,
  updated,
}: {
  original: Record<string, unknown>;
  updated: Record<string, unknown>;
}) => {
  const diff = Diff.updatedDiff(original, updated);

  return Object.fromEntries(
    Object.entries(diff).filter(([_, value]) => value !== undefined),
  );
};
